import { JsonView } from "@uiw/react-json-view"
import { darkTheme } from "@uiw/react-json-view/dark"
import { lightTheme } from "@uiw/react-json-view/light"
import { dump } from "js-yaml"
import { useEffect, useState } from "react"

const mq = matchMedia("(prefers-color-scheme: dark)")

export function JsonViewBlock({ value }: { value: unknown }) {
  const [dark, setDark] = useState(mq.matches)
  useEffect(() => {
    const on = () => setDark(mq.matches)
    mq.addEventListener("change", on)
    return () => mq.removeEventListener("change", on)
  }, [])
  return (
    <JsonView
      value={value as object}
      collapsed={1}
      displayDataTypes={false}
      style={{
        ...(dark ? darkTheme : lightTheme),
        fontFamily: "var(--font-mono)",
        fontSize: "0.75rem",
      }}
    />
  )
}

const MAX_DEPTH = 12

export function JsonBlock({
  value,
  label,
  className,
}: {
  value: unknown
  label?: string
  className?: string
}) {
  return (
    <div className={`min-w-0 overflow-x-auto font-mono text-xs ${className ?? ""}`}>
      {label && <p className="mb-1 text-muted-foreground">{label}</p>}
      <pre className="whitespace-pre">{dumpYaml(value)}</pre>
    </div>
  )
}

function dumpYaml(value: unknown): string {
  if (typeof value === "string") {
    try {
      return dump(JSON.parse(value), { lineWidth: Infinity, noRefs: true })
    } catch {
      return value
    }
  }
  return dump(truncate(value, 0), { lineWidth: Infinity, noRefs: true })
}

function truncate(value: unknown, depth: number): unknown {
  if (depth >= MAX_DEPTH) {
    return "…"
  }
  if (Array.isArray(value)) {
    return value.map((v) => truncate(v, depth + 1))
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, truncate(v, depth + 1)]))
  }
  return value
}
